import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '@components/atoms/button';
import { CloseIcon } from '@components/icons/close-icon';
import type { DocsVerificationLivenessTagErrorEnum } from '@models/enums';
import { DocsVerificationTabEnum } from '@models/enums';
import type { FileWithPreview, ThemeProps } from '@models/types';
import type { RootState } from '@modules/docs-verification-widget/store';
import useMediaQuery from '@mui/material/useMediaQuery';
import useTheme from '@mui/styles/useTheme';
import { docsVerificationSlice } from '@store/slices/docs-verification/docs-verification-slice';
import cn from 'classnames';

import { Card } from '../../../card';

import { LivenessProcess } from './step/liveness-process';
import type { FaceDetectResult, LivelessProps } from './step/liveness-process/types';
import { LivenessStepsEnum } from './step/liveness-process/types';
import { LivenessContainer } from './styles';

export const Liveness: React.FC<LivelessProps> = ({ onFileChange, onSubmit, ...props }) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const theme = useTheme<ThemeProps>();
    const matchesMobileSm = useMediaQuery(`(max-width:${theme.breakPoints.sm})`);
    const files = useSelector((state: RootState) => state.docsVerification.documents.files).filter(
        (item) => item.tab === DocsVerificationTabEnum.BIOMETRICS,
    );
    const isSuccess = !!files.filter((item) => item.status.success).length;
    const isError = !!files.filter((item) => item.status.error).length;

    const [livenessStep, setLivenessStep] = useState<LivenessStepsEnum>(() => {
        if (files.length) {
            return LivenessStepsEnum.FINISH;
        }

        if (!isSuccess) {
            dispatch(docsVerificationSlice.actions.setIsCameraMode(true));
        }

        return LivenessStepsEnum.PROCESS;
    });
    const isCameraMode = useSelector((state: RootState) => state.docsVerification.isCameraMode);

    const handleSubmit = useCallback(
        (isFailedVerification?: boolean) => {
            onSubmit?.(isFailedVerification);
        },
        [onSubmit],
    );

    const handleFileChange = useCallback(
        async (
            file: FileWithPreview,
            tag?: DocsVerificationLivenessTagErrorEnum,
            faceDetectResult?: FaceDetectResult,
        ) => {
            const fileState = await onFileChange(file, tag, faceDetectResult);

            return fileState;
        },
        [onFileChange],
    );

    const handleClose = useCallback(() => {
        dispatch(docsVerificationSlice.actions.goBackStep());
    }, [dispatch]);

    const handleRedoPhotoClick = () => {
        const lastFile = files[files.length - 1];

        if (lastFile?.id) {
            dispatch(docsVerificationSlice.actions.deleteFileById(lastFile.id));
        }
        dispatch(docsVerificationSlice.actions.setIsCameraMode(true));
        setLivenessStep(LivenessStepsEnum.PROCESS);
    };

    const handleNextClick = () => {
        if (files.length === 0) {
            dispatch(docsVerificationSlice.actions.setIsCameraMode(true));
            setLivenessStep(LivenessStepsEnum.PROCESS);

            return;
        }
        if (isSuccess && onSubmit) {
            onSubmit(files[0].status.isFailedVerification);
        }
    };

    const message = useMemo(() => files.find((item) => item.status.error)?.status.message, [files]);

    return (
        <LivenessContainer>
            {livenessStep === LivenessStepsEnum.PROCESS && (
                <LivenessProcess
                    onFileChange={handleFileChange}
                    {...props}
                    onSubmit={handleSubmit}
                    onClose={handleClose}
                    onCameraErrorClose={handleClose}
                    className='liveness-process-container'
                />
            )}
            {livenessStep === LivenessStepsEnum.FINISH && (
                <Card
                    previews={files
                        ?.filter((item) => item.uploadedFile?.preview)
                        .map((item) => item.uploadedFile?.preview as string)}
                    message={message}
                    isLoading={false}
                    isError={isError}
                    isSuccess={isSuccess}
                    className='liveness-card'
                />
            )}
            {!isCameraMode && livenessStep !== LivenessStepsEnum.PROCESS && (
                <React.Fragment>
                    <div className='actions'>
                        {matchesMobileSm ? (
                            <React.Fragment>
                                {(files.length > 0 || isError) && (
                                    <Button
                                        variant={isError ? 'primary' : 'secondary'}
                                        onClick={handleRedoPhotoClick}
                                        size='xl'
                                        className={cn('button', { secondary: !isError })}
                                    >
                                        {t('common.redoPhoto')}
                                    </Button>
                                )}
                                {isSuccess && (
                                    <Button
                                        variant='primary'
                                        onClick={handleNextClick}
                                        size='xl'
                                        className='button'
                                    >
                                        {t('common.continue')}
                                    </Button>
                                )}
                            </React.Fragment>
                        ) : (
                            <React.Fragment>
                                {files.length > 0 && (
                                    <Button
                                        variant='error-secondary'
                                        onClick={handleRedoPhotoClick}
                                        size='xl'
                                        className={cn('button', {
                                            'full-width-button': isError,
                                        })}
                                        leftAddon={<CloseIcon className='retake-icon' />}
                                    >
                                        {t('common.retake')}
                                    </Button>
                                )}
                                {isSuccess && (
                                    <Button
                                        variant='primary'
                                        onClick={handleNextClick}
                                        size='xl'
                                        className={cn('button', {
                                            'full-width-button': files.length === 0,
                                        })}
                                    >
                                        {t('common.continue')}
                                    </Button>
                                )}
                            </React.Fragment>
                        )}
                    </div>
                </React.Fragment>
            )}
        </LivenessContainer>
    );
};
